import { Box, Text } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import PrimaryButton from "components/Button/PrimaryButton";
import Button from "components/Button";
import { Step, Steps, useSteps } from "chakra-ui-steps";
import { useEffect, useCallback } from "react";
import { Modal } from "antd";
import FormComposicoesPagamentos from "./FormComposicoesPagamentos";
import Confirmacao from "./Confirmacao";
import { FormAcordo } from "features/acordo/typings";
import { ContasComposicoesAgrupadasResponse } from "features/cobranca/typings";
import moment from "moment";
import { useLoading } from "components/GlobalLoading";
import { useMemo, useState } from "react";
import * as mathjs from "mathjs";
import { UseQueryResult } from "react-query";
import PagamentoParcelas from "./PagamentoParcelas";
import useParametrosCobranca from "features/condominio/hooks/useParametrosCobranca";
import { useCurrentTenant } from "core/features/auth/tenant.store";

interface ModalNovoAcordoProps {
  isOpen: boolean;
  unidadeId: string;
  onClose: () => any;
  onSubmit: (data: FormAcordo) => any;
  fetchComposicoesHook: (
    params: any,
    body?: any
  ) => UseQueryResult<ContasComposicoesAgrupadasResponse[], unknown>;
  fetchComposicoesHookParams: any;
  fetchComposicoesHookBody?: any;
  isLoadingSave?: boolean;
  canEditComposicoes?: boolean;
}

interface StepItem {
  label: string;
  content?: JSX.Element;
}

function ModalNovoAcordo({
  isOpen,
  onClose,
  unidadeId,
  onSubmit,
  fetchComposicoesHook,
  fetchComposicoesHookParams,
  fetchComposicoesHookBody,
  isLoadingSave,
  canEditComposicoes = true,
}: ModalNovoAcordoProps) {
  const [
    parcelasDistribuidasCorretamente,
    setParcelasDistribuidasCorretamente,
  ] = useState(false);
  const { tenant } = useCurrentTenant();
  const { control, handleSubmit, watch, setValue } = useForm<FormAcordo>({
    reValidateMode: "onChange",
    mode: "onTouched",
    defaultValues: {
      composicoes: [],
      dataPrimeiraParcela: moment().add(1, "M").format("YYYY-MM-DD"),
      unidadeId,
      gerarBoletoAutomatico: true,
      enviarBoletoEmail: true,
      quantidadeParcela: 10,
      parcelas: [],
    },
  });

  const {
    data: parametrosCobranca,
    isLoading: isLoadingParametros,
    isFetching: isFetchingParametros,
  } = useParametrosCobranca(tenant.id);

  const { isFetching: isLoadingComposicoes, refetch } = fetchComposicoesHook(
    fetchComposicoesHookParams,
    fetchComposicoesHookBody
  );

  const { nextStep, prevStep, activeStep } = useSteps({
    initialStep: 0,
  });

  const setComposicoes = useCallback(
    (composicoes: ContasComposicoesAgrupadasResponse[] | undefined) => {
      setValue(
        "composicoes",
        composicoes?.map((item) => ({
          categoriaFinanceiraId: item.categoriaFinanceiraId,
          operacao: item.operacao,
          valor: item.valor,
          descricao: "",
        })) || []
      );
    },
    [setValue]
  );

  useEffect(() => {
    if (isOpen) {
      setValue("composicoes", []);
      refetch().then(({ data }) => {
        setComposicoes(data);
      });
    }
  }, [isOpen, refetch, setComposicoes, setValue]);

  useEffect(() => {
    if (parametrosCobranca) {
      setValue("taxaMulta", parametrosCobranca.taxaMulta);
      setValue("taxaJurosMensal", parametrosCobranca.juros[0]?.valor);
    }
  }, [parametrosCobranca, setValue]);

  const form = watch();

  const totalComposicoes = form.composicoes.reduce(
    (agg, item) => agg + (item.valor * item.operacao || 0),
    0
  );

  const total = useMemo(() => {
    let _total = 0;

    _total += totalComposicoes;

    if (form.desconto) {
      _total -= form.desconto;
    }

    if (form.entrada?.valor) {
      _total -= Number(form.entrada.valor);
    }

    return mathjs.floor(_total, 3) as number;
  }, [form, totalComposicoes]);

  const steps: Array<StepItem> = [
    {
      label: "Composições",
      content: (
        <FormComposicoesPagamentos
          setValue={setValue}
          control={control}
          watch={watch}
          canEditComposicoes={canEditComposicoes}
        />
      ),
    },
    {
      label: "Pagamento e Parcelas",
      content: (
        <PagamentoParcelas
          setValue={setValue}
          control={control}
          watch={watch}
          total={mathjs.floor(total, 3) as number}
          totalComposicoes={mathjs.floor(totalComposicoes, 3) as number}
          onDistributionChange={setParcelasDistribuidasCorretamente}
        />
      ),
    },
    {
      label: "Confirmação",
      content: (
        <Confirmacao
          unidadeId={unidadeId}
          total={total}
          watch={watch}
          control={control}
        />
      ),
    },
  ];

  const _onSubmit = (data: FormAcordo) => {
    onSubmit({
      ...data,
      entrada: data.entrada?.valor ? data.entrada : null,
      composicoes: data.composicoes.filter((c) => c.categoriaFinanceiraId),
      unidadeId,
    });
  };

  useLoading(isLoadingSave || isLoadingComposicoes);

  const NUM_STEPS: number = steps.length;
  const isLastStep = activeStep === NUM_STEPS - 1;

  const isMissingComposicoes =
    form.composicoes.length < 1 ||
    form.composicoes.some(
      (composicao) => !composicao.categoriaFinanceiraId || !composicao.valor
    );

  const isValidEntrada =
    !form.entrada?.valor ||
    (!!form.entrada?.valor &&
      form.entrada?.contaBancariaId &&
      form.entrada.valor <= totalComposicoes);

  const isMissingValorParcelas =
    activeStep === 1 && !parcelasDistribuidasCorretamente;

  const isDisabledNextButton = isMissingComposicoes || isMissingValorParcelas;

  return (
    <Modal
      width="1259px"
      visible={isOpen}
      title={<Text fontSize={20}>Novo Acordo</Text>}
      footer={
        <Box
          display="flex"
          gridGap={6}
          justifyContent="flex-end"
          flex={1}
          mt={8}
        >
          <Button onClick={() => (activeStep !== 0 ? prevStep() : onClose())}>
            {activeStep === 0 ? "Fechar" : "Voltar"}
          </Button>

          <PrimaryButton
            isLoading={
              isLoadingParametros ||
              isFetchingParametros ||
              isLoadingComposicoes ||
              isLoadingSave
            }
            disabled={isDisabledNextButton || !isValidEntrada}
            title={
              isMissingComposicoes
                ? "Preencha todas as categorias"
                : isMissingValorParcelas
                ? "Distribua todo o valor das parcelas"
                : !isValidEntrada
                ? "Preencha a entrada"
                : undefined
            }
            onClick={isLastStep ? handleSubmit(_onSubmit) : () => nextStep()}
          >
            {isLastStep ? "Finalizar" : "Avançar"}
          </PrimaryButton>
        </Box>
      }
      onCancel={onClose}
    >
      <form onSubmit={(ev) => handleSubmit(_onSubmit)(ev)}>
        <>
          <br />

          <Steps activeStep={activeStep} colorScheme={"blue"}>
            {steps.map((step, index) => (
              <Step key={index} fontSize={14} label={step.label}>
                {step.content}
              </Step>
            ))}
          </Steps>
        </>
      </form>
    </Modal>
  );
}

export default ModalNovoAcordo;
